import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause } from 'lucide-react';
import { cn } from '@/lib/utils';

interface Props {
  src: string;
  durationMs?: number | null;
  isMine: boolean;
}

// Task #1660 — Player audio compatto per messaggi vocali.
// Stile WhatsApp/Telegram: play/pause + progress bar + timecode. Audio è
// pre-caricato (`preload="metadata"`) per timing accurato; il player non
// blocca il render della lista.

export function VoicePlayer({ src, durationMs, isMine }: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState((durationMs || 0) / 1000);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const onTime = () => setCurrent(a.currentTime);
    const onMeta = () => { if (Number.isFinite(a.duration)) setDuration(a.duration); };
    const onEnd = () => { setPlaying(false); setCurrent(0); };
    a.addEventListener('timeupdate', onTime);
    a.addEventListener('loadedmetadata', onMeta);
    a.addEventListener('ended', onEnd);
    return () => {
      a.removeEventListener('timeupdate', onTime);
      a.removeEventListener('loadedmetadata', onMeta);
      a.removeEventListener('ended', onEnd);
    };
  }, []);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); setPlaying(false); }
    else { a.play().then(() => setPlaying(true)).catch(() => setPlaying(false)); }
  };

  const pct = duration > 0 ? Math.min(100, (current / duration) * 100) : 0;
  const fmt = (s: number) => {
    if (!Number.isFinite(s)) return '00:00';
    const total = Math.floor(s);
    return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
  };

  return (
    <div className={cn('flex items-center gap-2 min-w-[200px] max-w-[280px]')}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <button
        type="button"
        onClick={toggle}
        className={cn(
          'h-8 w-8 rounded-full flex items-center justify-center shrink-0 transition-colors',
          isMine ? 'bg-white/20 hover:bg-white/30 text-white' : 'bg-blue-600 hover:bg-blue-700 text-white',
        )}
        aria-label={playing ? 'Pausa' : 'Riproduci'}
      >
        {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5 ml-0.5" />}
      </button>
      <div className="flex-1 min-w-0">
        <div className={cn('h-1 rounded-full overflow-hidden', isMine ? 'bg-white/30' : 'bg-gray-200 dark:bg-gray-700')}>
          <div className={cn('h-full', isMine ? 'bg-white' : 'bg-blue-600')} style={{ width: `${pct}%` }} />
        </div>
        <div className={cn('text-[10px] mt-0.5 font-mono', isMine ? 'text-blue-100' : 'text-muted-foreground')}>
          {fmt(current)} / {fmt(duration)}
        </div>
      </div>
    </div>
  );
}
